
import React, { useState } from "react";
import { TrendingUp } from "lucide-react";
import { fetchLongitudinalSentiment } from "../../api/emotionalConnect";
import { useQuery } from "@tanstack/react-query";
import LoadingCard from "./LoadingCard";
import PostCallFallback from "./PostCallFallback";

const LongitudinalSentiment = ({
  appointmentId,
  username,
  callEndTime,
  postCallWindowExpired = false,
  finalFetchStatus = "idle",
}) => {
  const [openProof, setOpenProof] = useState({});

  const endTimestamp = callEndTime
    ? new Date(callEndTime).getTime()
    : NaN;

  const deadlineExpired =
    Boolean(postCallWindowExpired) ||
    (Number.isFinite(endTimestamp) &&
      Date.now() >= endTimestamp + 10 * 60 * 1000);

  const {
    data: total_data,
    isLoading,
    isFetching,
    error,
    refetch,
  } = useQuery({
    queryKey: ["longitudinalSentiment", appointmentId, username],
    queryFn: ({ signal }) =>
      fetchLongitudinalSentiment(
        `${username}_${appointmentId}_longitudinal_sentiment`,
        username,
        signal
      ),
    enabled: Boolean(username && appointmentId && !deadlineExpired),
    retry: 3,
    staleTime: Infinity,
    gcTime: Infinity,
    retryOnMount: true,
    refetchOnMount: "always",
    refetchOnWindowFocus: false,
    refetchInterval: false,
  });

  const data = total_data?.data?.data || total_data?.data || {};

  const finalFetchPending =
    deadlineExpired &&
    !total_data &&
    finalFetchStatus === "fetching";

  const toggleProof = (key) => {
    setOpenProof((previous) => ({
      ...previous,
      [key]: !previous[key],
    }));
  };

  if (
    isLoading ||
    (!deadlineExpired && isFetching) ||
    finalFetchPending
  ) {
    return (
      <LoadingCard message="Longitudinal sentiment in progress…" />
    );
  }


  // Show the fallback only for an actual request failure.
  // An empty or partial successful response still renders the dashboard.
  if (error && !total_data) {
    return (
      <PostCallFallback
        title="Longitudinal Sentiment"
        icon={<TrendingUp size={20} />}
        message={
          deadlineExpired
            ? "No longitudinal sentiment data available."
            : "Longitudinal sentiment data is not available yet. You can try again."
        }
        deadlineExpired={deadlineExpired}
        onRetry={() => refetch()}
        isRetrying={isFetching}
        retryLabel="Retry Longitudinal Sentiment"
        retryingLabel="Retrying Longitudinal Sentiment…"
      />
    );
  }

  // Keep the dashboard visible when individual fields are missing.
  return (
    <div className="rounded-lg border border-gray-100 bg-white p-8">
      <div className="mx-auto max-w-5xl">
        <h3 className="mb-6 text-2xl font-medium text-gray-800">
          Longitudinal Sentiment (for last 3 Appointments)
        </h3>

        <h4 className="mb-1 font-semibold text-gray-500">
          Confidence : {data.confidence?.score ?? "n/a"}
        </h4>

        <div className="mt-2 rounded border-l-4 border-blue-100 bg-gray-50 p-3 text-sm text-gray-500">
          {data.confidence?.proof}
        </div>

        <div className="divide-y divide-gray-100">
          <section className="py-6">
            <div className="md:flex md:items-start md:justify-between">
              <div className="md:pr-8">
                <h4 className="mb-2 font-semibold text-blue-700">
                  Overall summary
                </h4>

                <div className="text-gray-800">
                  {data.overall_summary?.statement}
                </div>

                {openProof.overall_summary && (
                  <div className="mt-2 rounded border-l-4 border-blue-100 bg-gray-50 p-3 text-sm text-gray-700">
                    {data.overall_summary?.proof}
                  </div>
                )}
              </div>

              <div className="mt-4 md:mt-0">
                <button
                  type="button"
                  onClick={() => toggleProof("overall_summary")}
                  className="flex h-8 w-32 items-center justify-center whitespace-nowrap rounded bg-blue-600 px-3 text-sm text-white hover:bg-blue-700"
                >
                  {openProof.overall_summary
                    ? "Hide conversation"
                    : "Show conversation"}
                </button>
              </div>
            </div>
          </section>

          <section className="py-6">
            <h4 className="mb-2 font-semibold text-blue-700">
              Sentiment trend
            </h4>

            <div className="space-y-3">
              {["positive", "negative"].map((key) => (
                <div key={key}>
                  <div className="font-medium capitalize text-gray-800">
                    {key} —{" "}
                    {data.sentiment_trend?.[key]?.score ?? "N/A"}%
                  </div>

                  {data.sentiment_trend?.[key]?.statement && (
                    <div className="text-sm text-gray-600">
                      {data.sentiment_trend[key].statement}
                    </div>
                  )}

                  <div className="mt-2 rounded border-l-4 border-blue-100 bg-gray-50 p-3 text-sm text-gray-700">
                    {data.sentiment_trend?.[key]?.proof}
                  </div>
                </div>
              ))}
            </div>
          </section>

          <section className="py-6">
            <h4 className="mb-2 font-semibold text-blue-700">
              Self perception of progress
            </h4>

            <div className="text-gray-800">
              {data.self_perception_of_progress?.statement}
            </div>

            <div className="mt-2 rounded border-l-4 border-blue-100 bg-gray-50 p-3 text-sm text-gray-700">
              {data.self_perception_of_progress?.proof}
            </div>
          </section>
        </div>
      </div>
    </div>
  );
};

export default LongitudinalSentiment;
